import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from 'styled-components';

const formatCategoryName = category => {
  if (!category) return 'General';
  return category
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

const CoreKnowledgeList = ({ groupedData, searchTerm }) => {
  const theme = useTheme();
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});

  if (!groupedData || Object.keys(groupedData).length === 0) {
    if (searchTerm) {
      return (
        <Typography
          sx={{
            mt: 1
          }}
        >
          No results found for "{searchTerm}"
        </Typography>
      );
    }
    return null;
  }

  const handleCategoryChange = category => (event, isExpanded) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: isExpanded
    }));
  };

  const handleQuestionToggle = questionId => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const categories = Object.entries(groupedData);

  return (
    <Box sx={{ pt: 1 }}>
      {categories.map(([category, items]) => (
        <Accordion
          key={category}
          expanded={expandedCategories[category] || false}
          onChange={handleCategoryChange(category)}
          sx={{
            mb: 1,
            ':before': { display: 'none' },
            border: `1px solid ${theme?.border?.primary}`,
            borderRadius: 2,
            backgroundColor: theme?.background?.secondary
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: '18px' }} />}
            sx={{
              '& .MuiAccordionSummary-content': { my: 0.5 }
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 'bold',
                color: theme?.text?.heading
              }}
            >
              {formatCategoryName(category)} ({items.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1, px: 1 }}>
            {items.map(item => {
              const questionId = `${category}-${item.id || item.questionId}`;
              const isExpanded = expandedQuestions[questionId] || false;

              return (
                <Box
                  key={questionId}
                  sx={{
                    borderBottom: `1px solid ${theme?.border?.primary}`,
                    '&:last-child': { borderBottom: 'none' }
                  }}
                >
                  <Box
                    onClick={() => handleQuestionToggle(questionId)}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      px: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme?.background?.hover
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: theme?.text?.primary,
                        wordBreak: 'break-word',
                        flex: 1
                      }}
                    >
                      {item.question}
                    </Typography>
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 16,
                        transform: isExpanded
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        ml: 1,
                        flexShrink: 0
                      }}
                    />
                  </Box>
                  {isExpanded && (
                    <Box
                      sx={{
                        px: 2,
                        pb: 1.5,
                        pt: 0.5,
                        backgroundColor: theme?.background?.tertiary
                      }}
                    >
                      <Typography
                        component="div"
                        fontStyle="italic"
                        sx={{
                          fontSize: 12,
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {item.answer || 'No answer available'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default CoreKnowledgeList;
